import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { klingelnbergLogo } from './logosBase64';

const generateQuotationPdf = (enquiry, quotationMetadata) => {
    if (!enquiry || !enquiry.offerDetails) {
        alert('Invalid enquiry data for quotation.');
        return;
    }

    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const { offerDetails, customer, enquiryNumber, machineManufacturer, machineModel, machineSrNo } = enquiry;

    // Define standard variables
    const currency = offerDetails.currency || 'INR';
    let items = [];
    let totalAmount = 0;
    
    // Fallback quotation number based on offer strategy
    const quotationNumber = offerDetails?.importedOffer?.klQuotationNumber || enquiryNumber || 'DRAFT-001';

    // Pre-calculate POS mapping
    const isLocalOffer = offerDetails.offerType === 'Local Manufacturing';
    const posMapping = {};
    let currentPos = 10;

    (enquiry.items || []).forEach((item, index) => {
        const isSelected = isLocalOffer
            ? item.localOffer?.isSelected !== false
            : item.importedOffer?.isSelected !== false;

        if (isSelected) {
            const customPos = isLocalOffer ? item.localOffer?.pos : item.importedOffer?.pos;
            posMapping[index] = customPos || currentPos;
            currentPos += 10;
        } else {
            posMapping[index] = '(Excluded)';
        }
    });

    // Helper to build the exact description format
    const buildDescription = (item, isLocal) => {
        const compNo = item.component_drawing_no ? `Component drawing No: ${item.component_drawing_no}` : '';
        const clampDia = item.clamping_dia ? `Clamping Dia: ${item.clamping_dia}` : '';
        const machineStr = (item.machine || enquiry.machineModel) ? `Machine - ${item.machine || enquiry.machineModel}` : '';
        let feasDesc = item.feasibilityStudy?.feasibilityDescription || '';
        const selectedFeasibilityRows = (item.feasibilityStudy?.partsTable || [])
            .filter((row) => row.isChecked)
            .map((row, idx) => {
                const details = [
                    row.name,
                    
                ].filter(Boolean).join(', ');

            return `${idx + 1}. ${details}`;
        });

        // Resolve dynamic POS placeholders like {{POS:1}}
        if (feasDesc) {
            feasDesc = feasDesc.replace(/\{\{POS:(\d+)\}\}/g, (match, p1) => {
                const uiIndex = parseInt(p1, 10) - 1;
                const mappedPos = posMapping[uiIndex];
                return mappedPos !== undefined ? mappedPos : match;
            });
        }

        const origin = isLocal ? 'Origin of fixture - India' : 'Origin of fixture - Imported';

        // Filter out empty lines for the top section, but keep empty strings for spacing
        const parts = [];
        if (compNo) parts.push(compNo);
        if (clampDia) parts.push(clampDia);
        if (machineStr) parts.push(machineStr);
        if (feasDesc) {
            parts.push('');
            parts.push(feasDesc);
        }
        if (selectedFeasibilityRows.length > 0) {
            parts.push('');
            parts.push('Selected Feasibility Items:');
            parts.push(selectedFeasibilityRows.join('\n'));
        }
        parts.push('');
        parts.push(origin);
        return parts.join('\n').trim();
    };

    if (offerDetails.offerType === 'Imported') {
        items = (enquiry.items || []).reduce((acc, item, index) => {
            if (item.importedOffer?.isSelected === false) return acc;

            // Calculate net value
            const pricePerUnit = Number(item.importedOffer?.pricePerUnit || 0);
            const qty = Number(item.quantity || 1);
            const netVal = pricePerUnit * qty;
            totalAmount += netVal;
            
            const description = buildDescription(item, false);
            
            acc.push([
                posMapping[index],
                description,
                qty,
                'Set',
                currency,
                pricePerUnit.toFixed(2),
                netVal.toFixed(2)
            ]);
            return acc;
        }, []);
    } else if (offerDetails.offerType === 'Local Manufacturing') {
        items = (enquiry.items || []).reduce((acc, item, index) => {
            if (item.localOffer?.isSelected === false) return acc;

            const price = Number(item.localOffer?.calculatedPrice || 0);
            const qty = Number(item.quantity || 1);
            const netVal = price * qty;
            totalAmount += netVal;
            
            const description = buildDescription(item, true);
            
            acc.push([
                posMapping[index],
                description,
                qty,
                'Set',
                currency,
                price.toFixed(2),
                netVal.toFixed(2)
            ]);
            return acc;
        }, []);
    }

    // --- REUSABLE HEADER & FOOTER FUNCTION ---
    const addHeaderAndFooter = (doc, pageNumber, totalPages) => {
        // --- HEADER ---
        
        // Klingelnberg Logo on top left
        try {
            // Adjust the dimensions as per the actual logo aspect ratio
            doc.addImage(klingelnbergLogo, 'JPEG', 14, 10, 20, 13);
        } catch (e) {
            console.error("Error adding logo:", e);
        }

        // Top Right Header Elements (Höfler text logo placeholder)
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(14);
        doc.setTextColor(100, 100, 100);
        doc.text('HÖFLER', pageWidth - 14, 15, { align: 'right' });
        
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7);
        doc.text('A BRAND OF KLINGELNBERG', pageWidth - 14, 19, { align: 'right' });

        // --- FOOTER ---
        const footerY = pageHeight - 45;
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.2);
        doc.line(14, footerY, pageWidth - 14, footerY); 

        const f = quotationMetadata?.footer || {};
        doc.setFontSize(6);
        doc.setFont('helvetica', 'bold');
        doc.text(f.companyName || '', 14, footerY + 5);

        doc.setFont('helvetica', 'normal');
        
        // Left Column: Corporate Office
        doc.text(f.corporateOfficeTitle || '', 14, footerY + 9);
        const corpOfficeLines = Array.isArray(f.corporateOffice) ? f.corporateOffice : [];
        corpOfficeLines.forEach((line, idx) => doc.text(line, 14, footerY + 13 + (idx * 4)));

        // Middle Column: Tech Centre
        doc.text(f.technologyCentreTitle || '', 85, footerY + 9);
        const techCentreLines = Array.isArray(f.technologyCentre) ? f.technologyCentre : [];
        techCentreLines.forEach((line, idx) => doc.text(line, 85, footerY + 13 + (idx * 4)));

        // Right Column: Tax/Legal Info
        const companyDetailsLines = Array.isArray(f.companyDetails) ? f.companyDetails : [];
        companyDetailsLines.forEach((line, idx) => doc.text(line, 155, footerY + 9 + (idx * 4)));

        // Absolute Bottom Page Data
        doc.setFontSize(7);
        doc.text(quotationNumber, 14, pageHeight - 8);
        doc.text(`Page ${pageNumber} of ${totalPages}`, pageWidth / 2, pageHeight - 8, { align: 'center' });
        const timeStr = new Date().toLocaleString('en-US', { hour: 'numeric', minute: 'numeric', hour12: true });
        const dateStr = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' }).replace(/\//g, '-');
        doc.text(`${dateStr} ${timeStr}`, pageWidth - 14, pageHeight - 8, { align: 'right' });
    };

    // --- MAIN DOCUMENT CONTENT ---
    let startY = 32;

    // Quotation Title
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('Quotation', pageWidth / 2, startY, { align: 'center' });
    startY += 5;

    // Box 1: Category, Quotation No, Date
    doc.setLineWidth(0.6);
    doc.setDrawColor(0, 0, 0);
    doc.roundedRect(14, startY, pageWidth - 28, 8, 2, 2, 'S');
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('Category Code: CGWH', 20, startY + 5.5);
    doc.setFont('helvetica', 'bold');
    doc.text(`Quotation No: ${quotationNumber}`, pageWidth / 2, startY + 5.5, { align: 'center' });
    doc.setFont('helvetica', 'normal');
    doc.text(`Date: ${new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }).replace(/ /g, '-')}`, pageWidth - 20, startY + 5.5, { align: 'right' });
    
    startY += 10;

    // Boxes 2 & 3: CUSTOMER and QUOTE TO
    const boxWidth = (pageWidth - 28 - 4) / 2;
    // Left Box
    doc.roundedRect(14, startY, boxWidth, 32, 4, 4, 'S');
    // Right Box
    doc.roundedRect(14 + boxWidth + 4, startY, boxWidth, 32, 4, 4, 'S');
    
    // Customer Details
    const custName = customer?.label || customer?.customer_name || 'Valued Customer';
    
    // Left Box Content
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.text('CUSTOMER', 20, startY + 6);
    
    doc.setTextColor(0, 0, 0);
    doc.text(custName, 20, startY + 12);
    
    // Try to format address nicely
    const addressLines = customer?.full_address ? doc.splitTextToSize(customer.full_address, boxWidth - 12) : ['N.A.'];
    doc.text(addressLines, 20, startY + 17);

    // Right Box Content
    doc.setTextColor(100, 100, 100);
    doc.text('QUOTE TO', 14 + boxWidth + 4 + 6, startY + 6);
    
    doc.setTextColor(0, 0, 0);
    doc.text(customer?.contact_name || 'N.A.', 14 + boxWidth + 4 + 6, startY + 12);
    doc.text(`Mobile: ${customer?.phone_number || 'N.A.'}`, 14 + boxWidth + 4 + 6, startY + 17);
    doc.text(`Ph. No: N.A.`, 14 + boxWidth + 4 + 6, startY + 22);
    
    // Check if email needs to be split
    const emailStr = customer?.email || 'N.A.';
    if (emailStr.length > 30) {
        doc.text(`E-Mail: ${emailStr.substring(0, 30)}...`, 14 + boxWidth + 4 + 6, startY + 27);
    } else {
        doc.text(`E-Mail: ${emailStr}`, 14 + boxWidth + 4 + 6, startY + 27);
    }

    startY += 36;

    // Reference Line
    doc.setFont('helvetica', 'normal');
    const contactName = customer?.contact_name || 'N.A.';
    const enquiryDate = (enquiry.created_date_time || enquiry.createdAt) ? new Date(enquiry.created_date_time || enquiry.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }).replace(/ /g, '-') : new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }).replace(/ /g, '-');
    
    // Format machine details
    const mfg = machineManufacturer?.label || '';
    const model = machineModel || '';
    const srNo = machineSrNo ? `(Sr: ${machineSrNo})` : '';
    const machineStr = `${mfg} ${model} ${srNo}`.trim() || 'Rapid1250';
    
    doc.text(`Reference: Enquiry from ${contactName} dtd. ${enquiryDate}  for Machine no. :${machineStr}`, 14, startY);

    startY += 6;
    // --- APPEND TERMS & CONDITIONS TO MAIN TABLE ---
    let termsBlocks = [];
    if (quotationMetadata) {
        const formatStringOrArray = (data) => {
            const str = Array.isArray(data) ? data.join('\n') : (data || '');
            return str.replace(/\t/g, ' ');
        };
        
        termsBlocks.push(`${quotationMetadata.paymentTermTitle || 'PAYMENT TERM:'}\n${formatStringOrArray(quotationMetadata.paymentTerm)}`);
        termsBlocks.push(`${quotationMetadata.deliveryTimeTitle || 'DELIVERY TIME'}\n${formatStringOrArray(quotationMetadata.deliveryTime)}`);
        termsBlocks.push(`${quotationMetadata.shipViaTitle || 'Ship Via:'}\n${formatStringOrArray(quotationMetadata.shipVia)}`);
        termsBlocks.push(`${quotationMetadata.incotermsTitle || 'Incoterms:'}\n${formatStringOrArray(quotationMetadata.incoterms)}`);
        termsBlocks.push(`${quotationMetadata.otherTermsAndConditionsTitle || 'Other Terms & Conditions:'}\n${formatStringOrArray(quotationMetadata.otherTermsAndConditions)}`);
        
        const rbClause = quotationMetadata.russiaBelarusClause;
        const rbTitle = quotationMetadata.russiaBelarusClauseTitle || '- No-Russia and Belarus Clause:';
        if (Array.isArray(rbClause) && rbClause.length > 0) {
            termsBlocks.push(`${rbTitle}\n${formatStringOrArray(rbClause[0])}`);
            for (let i = 1; i < rbClause.length; i++) {
                termsBlocks.push(formatStringOrArray(rbClause[i]));
            }
        } else if (rbClause) {
            termsBlocks.push(`${rbTitle}\n${formatStringOrArray(rbClause)}`);
        }
    }

    termsBlocks.forEach((block) => {
        items.push([
            '',
            block,
            '',
            '',
            '',
            '',
            ''
        ]);
    });

    // Add 2 line spacing after termBlocks
    items.push(['', '', '', '', '', '', '']);
    items.push(['', '', '', '', '', '', '']);

    let bankDetailsContent = '';

    if (quotationMetadata && quotationMetadata.bankDetails) {
        const bd = quotationMetadata.bankDetails;
        const bdTitle = quotationMetadata.bankDetailsTitle || '';
        const addressLines = Array.isArray(bd.address) ? bd.address.join('\n                  ') : (bd.address || '');
        bankDetailsContent = `${bdTitle}\n` +
            `${bd.accountName || ''}\n` +
            `Bank        : ${bd.bank || ''}\n` +
            `Currency  : ${bd.currency || ''}\n` +
            `Branch     : ${bd.branch || ''}\n` +
            `Address   : ${addressLines}\n` +
            `IFSC         : ${bd.ifsc || ''}\n` +
            `A/C No.    : ${bd.accountNumber || ''}\n` +
            `SWIFT      : ${bd.swift || ''}\n` +
            `Ph. No.     : ${bd.phone || ''}`;
        bankDetailsContent = bankDetailsContent.replace(/\t/g, ' ');
    }

    // Bank Details row
    items.push([
        '', // Empty Quote Posit column
        { 
            content: bankDetailsContent, 
            styles: { cellPadding: 3 } 
        }, 
        '',
        '',
        '',
        '',
        ''
    ]);

    // Append Final Total as the second to last row
    items.push([
        '', 
        '', 
        '', 
        '', 
        '', 
        { 
            content: `Total in ${currency}`, 
            styles: { fontStyle: 'bold', halign: 'right', valign: 'bottom', cellPadding: 3 } 
        }, 
        { 
            content: totalAmount.toFixed(2), 
            styles: { fontStyle: 'bold', halign: 'right', valign: 'bottom', cellPadding: 3 } 
        }
    ]);

    let eoeContent = '';
    if (quotationMetadata) {
        const eoeTitle = quotationMetadata.eoeTitle || '';
        const eoeLines = Array.isArray(quotationMetadata.eoe) ? quotationMetadata.eoe.join('\n') : (quotationMetadata.eoe || '');
        eoeContent = `${eoeTitle}\n${eoeLines}`.replace(/\t/g, ' ');
    }

    // Append E & OE as the last row
    items.push([
        {
            content: eoeContent,
            colSpan: 7,
            styles: { fontStyle: 'bold', halign: 'left', valign: 'middle', cellPadding: 2 }
        }
    ]);

    let pageMaxY = 0;

    doc.autoTable({
        startY: startY,
        head: [['Quote Position', 'Part no/ Description', 'Qty', 'UoQ', 'Currency', 'Unit Price', 'Total Amount']],
        body: items,
        theme: 'plain',
        rowPageBreak: 'avoid', // Prevent row splitting since we chunked the rows manually
        styles: {
            font: 'helvetica',
            fontSize: 8,
            cellPadding: 2,
            textColor: [0, 0, 0]
        },
        headStyles: {
            fillColor: false,
            textColor: [0, 0, 0],
            fontStyle: 'bold',
            halign: 'center'
        },
        columnStyles: {
            0: { cellWidth: 20 },
            1: { cellWidth: 70 },
            2: { cellWidth: 15 },
            3: { cellWidth: 15 },
            4: { cellWidth: 20 },
            5: { cellWidth: 25, halign: 'right' },
            6: { cellWidth: 25, halign: 'right' },
        },
        margin: { top: 35, bottom: 48, left: 14, right: 14 },
        willDrawCell: (data) => {
            // Handle bold labels in description column (index 1)
            if (data.section === 'body' && data.column.index === 1 && data.cell.text && data.cell.text.length > 0) {
                const labels = ["Component drawing No:", "Clamping Dia:", "Machine -"];
                const hasLabel = data.cell.text.some(line => 
                    labels.some(label => line.trim().startsWith(label))
                );
                
                if (hasLabel) {
                    // Store original text and clear it for autoTable drawing
                    data.cell.originalText = [...data.cell.text];
                    data.cell.text = []; 
                }
            }
        },
        didDrawPage: (data) => {
            // Draw a solid horizontal line at the end of the table on EVERY page
            doc.setLineWidth(0.2);
            doc.setDrawColor(0, 0, 0);

            // Use pageMaxY (actual bottom of the drawn cells) or fallback to data.cursor.y
            const bottomY = pageMaxY > 0 ? pageMaxY : data.cursor.y;
            
            // Extend by 0.1 on both sides to perfectly join with vertical borders
            doc.line(13.9, bottomY, pageWidth - 13.9, bottomY);

            // Reset for the next page
            pageMaxY = 0;
        },
        didDrawCell: (data) => {
            // Handle custom drawing for bold labels
            if (data.cell.originalText) {
                const x = data.cell.x + data.cell.padding('left');
                let y = data.cell.y + data.cell.padding('top');
                
                // Use consistent line height
                const fontSize = data.cell.styles.fontSize;
                doc.setFontSize(fontSize);
                const lineHeight = doc.internal.getLineHeight() / doc.internal.scaleFactor;
                
                const labels = ["Component drawing No:", "Clamping Dia:", "Machine -"];

                data.cell.originalText.forEach((line) => {
                    let matchedLabel = labels.find(l => line.trim().startsWith(l));
                    
                    if (matchedLabel) {
                        const splitIndex = line.indexOf(matchedLabel) + matchedLabel.length;
                        const labelPart = line.substring(0, splitIndex);
                        const valuePart = line.substring(splitIndex);

                        doc.setFont('helvetica', 'bold');
                        doc.text(labelPart, x, y, { baseline: 'top' });
                        
                        const labelWidth = doc.getTextWidth(labelPart);
                        doc.setFont('helvetica', 'normal');
                        doc.text(valuePart, x + labelWidth, y, { baseline: 'top' });
                    } else {
                        doc.setFont('helvetica', 'normal');
                        doc.text(line, x, y, { baseline: 'top' });
                    }
                    y += lineHeight;
                });
            }

            doc.setDrawColor(0, 0, 0);
            doc.setLineWidth(0.2);
            
            const cellBottomY = data.cell.y + data.cell.height;
            if (cellBottomY > pageMaxY) {
                pageMaxY = cellBottomY;
            }
            
            // Draw left vertical line for every cell
            doc.line(data.cell.x, data.cell.y, data.cell.x, cellBottomY);
            
            const colSpan = data.cell.colSpan || 1;
            const isLastColumn = data.column.index + colSpan === data.table.columns.length;

            // Draw right vertical line for the last column
            if (isLastColumn) {
                doc.line(data.cell.x + data.cell.width, data.cell.y, data.cell.x + data.cell.width, data.cell.y + data.cell.height);
            }
            
            // Determine X coordinates with slight extension for the outer borders to form perfect corners
            const startX = data.column.index === 0 ? data.cell.x - 0.1 : data.cell.x;
            const endX = isLastColumn ? data.cell.x + data.cell.width + 0.1 : data.cell.x + data.cell.width;

            // Draw top and bottom horizontal lines ONLY for the header row
            if (data.section === 'head') {
                doc.line(startX, data.cell.y, endX, data.cell.y);
                doc.line(startX, data.cell.y + data.cell.height, endX, data.cell.y + data.cell.height);
            }

            // Draw a top border for the last two rows (Total in INR and E&OE)
            if (data.section === 'body' && data.row.index >= data.table.body.length - 2) {
                doc.line(startX, data.cell.y, endX, data.cell.y);
            }
        }
    });

    let finalY = doc.lastAutoTable.finalY || startY + 10;

    // --- SIGN-OFF ---
    let signOffY = doc.lastAutoTable.finalY + 10;
    
    // Check if we need a new page for the sign-off (requires ~35 units + 50 units for footer margin)
    if (signOffY > pageHeight - 85) {
        doc.addPage();
        signOffY = 45; 
    }

    const sig = quotationMetadata?.signature || {};
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(sig.bestRegards || '', 14, signOffY);
    doc.setFont('helvetica', 'bold');
    doc.text(sig.companyName || '', 14, signOffY + 5);
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text(sig.electronicDocumentNote || '', 14, signOffY + 15);
    doc.text(sig.signedText || '', 14, signOffY + 20);
    doc.setFont('helvetica', 'bold');
    doc.text(`${sig.signatoryName || ''}${sig.signatoryPhone ? `(${sig.signatoryPhone})` : ''}`, 14, signOffY + 25);

    // Loop through generated pages to apply Headers & Footers
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        addHeaderAndFooter(doc, i, totalPages);
    }

    doc.save(`Quotation_${quotationNumber}.pdf`);
};

export default generateQuotationPdf;